import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription, throwError } from 'rxjs';
import { TableData } from '@dxo/ng-swift-ui/lib/components/table/components/table-base/table.types';
import {
  DocumentInfoResponse,
  DocumentsClient,
  DocumentSearchRequestDocumentCategory,
  PolicyClient,
  SearchSortDirection
} from '@benefitsui/app/api/benefits-clients';
import { CoverageDocumentData, DistinctCoverage, DocumentsComponentData } from './documents-page.types';
import { HttpErrorService } from '@benefitsui/app/services/http-error/http-error.service';
import { catchError, map } from 'rxjs/operators';

@Component({
  selector: 'app-documents-page',
  templateUrl: './documents-page.component.html',
  styleUrls: ['./documents-page.component.scss']
})
export class DocumentsPageComponent implements OnInit, OnDestroy {
  constructor(
    private documentClient: DocumentsClient,
    private errorService: HttpErrorService,
    private policyClient: PolicyClient
  ) {}

  hasServerError = false;
  hasEmptyDoc = false;
  messageHeader = 'PDF file not loading';
  messageBody = `We are working to correct an internal issue blocking this PDF from displaying.
  Apologies for the inconvenience and please check back later.`;
  isLoading = true;
  private subscriptions: Subscription[] = [];
  coverageData!: DistinctCoverage[];
  documentsData!: DocumentInfoResponse[];
  coverageDocumentDataArray: CoverageDocumentData[] = [];

  ngOnInit(): void {
    const coverage$ = this.policyClient.coverage().pipe(map((res) => res.data?.policyCoverages?.coverages));
    const documents$ = this.documentClient
      .search({
        policyIds: [],
        documentCategory: DocumentSearchRequestDocumentCategory.All,
        page: 1,
        pageSize: 100,
        sortDirection: SearchSortDirection.Ascending,
        year: undefined
      })
      .pipe(
        map((res) => {
          return res.data?.documents;
        }),
        catchError((err) => {
          this.errorService.preventNextErrorRedirect();
          this.hasServerError = true;
          return throwError(err);
        })
      );

    this.subscriptions.push(
      forkJoin([coverage$, documents$]).subscribe(
        (response) => {
          this.coverageData = (response[0] ?? []).map((rsp) => {
            return rsp;
          });
          const coverageDataId = 'documentCategory';
          const uniquecoverageData = [
            ...new Map(this.coverageData.map((item) => [item[coverageDataId], item])).values()
          ];
          this.documentsData = (response[1] ?? []).map((rsp) => {
            return rsp;
          });
          let totalDocLength = 0;
          uniquecoverageData.forEach((coverage) => {
            const dataLength = this.getLength(coverage.documentCategory ?? '');
            const dataToAdd = {
              coverageId: coverage.documentCategory ?? '',
              coverageDescription: coverage.policyDocsTable ?? '',
              document: this.getData(coverage.documentCategory ?? ''),
              documentLength: dataLength
            };
            totalDocLength += dataLength;
            this.coverageDocumentDataArray.push(dataToAdd);
          });
          const noticeToAdd = {
            coverageId: 'Notice',
            coverageDescription: 'Notices and agreements',
            document: this.getData('Notice'),
            documentLength: this.getLength('Notice')
          };
          if (noticeToAdd.documentLength > 0) {
            totalDocLength += noticeToAdd.documentLength;
            this.coverageDocumentDataArray.push(noticeToAdd);
          }
          if (totalDocLength === 0) {
            this.isLoading = false;
            this.hasEmptyDoc = true;
            this.messageHeader = 'Contract documents are currently unavailable';
            this.messageBody = 'Your contract documents are pending finalization and will be viewable soon.';
          }
        },
        () => {
          this.errorService.preventNextErrorRedirect();
          this.hasServerError = true;
        },
        () => {
          this.isLoading = false;
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => {
      s?.unsubscribe();
    });
  }

  filteredData(coverage: string): DocumentInfoResponse[] {
    if (coverage === 'PFML') {
      return this.documentsData.filter((x) => x.documentCategory === 'NYPFL' || x.documentCategory === 'PFML');
    } else if (coverage === 'All') {
      return this.documentsData;
    } else {
      return this.documentsData.filter((x) => x.documentCategory === coverage);
    }
  }

  getData(coverage: string): DocumentsComponentData {
    return this.transformData(this.filteredData(coverage));
  }

  getLength(coverage: string): number {
    if (coverage === 'PFML') {
      return this.documentsData.filter((x) => x.documentCategory === 'NYPFL' || x.documentCategory === 'PFML').length;
    } else {
      return this.documentsData.filter((x) => x.documentCategory === coverage).length;
    }
  }

  transformData(response: DocumentInfoResponse[]): DocumentsComponentData {
    const tableData: TableData = {
      headers: [{ text: 'Document name', width: '60%' }, { text: 'Effective date' }, { text: 'Actions' }],
      cells: []
    };

    for (const document of response) {
      tableData.cells.push([
        { data: document.documentTitle ?? '' },
        { data: document.documentDate ?? '' },
        {
          link: {
            download: false,
            href: document.fileUri ?? ''
          },
          display: 'View',
          icon: true,
          iconName: 'open_in_new'
        }
      ]);
    }
    return { tableData };
  }
}
